import { useCallback, useEffect, useRef, useState } from "react";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
  type Renderer,
} from "@googlemaps/markerclusterer";
import type { Itinerary } from "@/lib/api/schemas";
import { DAY_COLORS } from "@/lib/dayColors";

interface ItineraryMapProps {
  itinerary: Itinerary;
  googleMapsApiKey?: string;
  onItemClick?: (itemId: string) => void;
  // Hovered item (ephemeral). When set, the corresponding marker gets a
  // pulsing ring and the map re-centers on it.
  focusedItemId?: string | null;
  // Sticky-selected item. Info window stays open for this marker and the
  // marker is drawn larger.
  selectedItemId?: string | null;
  // When set, the map filters to just this day's markers and polyline
  // and refits to that day's bounds. Null/undefined = show everything.
  activeDayNumber?: number | null;
  // Called when a user taps a marker in overview mode (no active day).
  // Opens that day for focus instead of selecting the individual item,
  // which would otherwise show a number that doesn't exist in the
  // overview rendering.
  onSetActiveDay?: (dayNumber: number) => void;
  // Tailwind height class, defaults to h-72 (288px).
  heightClass?: string;
}

interface MapMarker {
  itemId: string;
  lat: number;
  lng: number;
  title: string;
  dayNumber: number;
  // 1-based position of this stop within its day — the number we
  // draw on the marker so it can be visually paired with the same
  // number shown on the itinerary card.
  stopIndex: number;
  time?: string;
  type: string;
  description?: string;
}

export function ItineraryMap({
  itinerary,
  googleMapsApiKey,
  onItemClick,
  focusedItemId,
  selectedItemId,
  activeDayNumber,
  onSetActiveDay,
  heightClass = "h-72",
}: ItineraryMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersByIdRef = useRef<Map<string, google.maps.Marker>>(new Map());
  const markerMetaByIdRef = useRef<Map<string, MapMarker>>(new Map());
  const polylinesRef = useRef<google.maps.Polyline[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const pulseCircleRef = useRef<google.maps.Circle | null>(null);
  const pulseStepRef = useRef(0);
  const pulseIntervalRef = useRef<number | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const boundsRef = useRef<google.maps.LatLngBounds | null>(null);
  const userLocationMarkerRef = useRef<google.maps.Marker | null>(null);
  const userLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const [geoDenied, setGeoDenied] = useState(false);
  const [recenterHint, setRecenterHint] = useState(false);
  const onItemClickRef = useRef(onItemClick);
  const onSetActiveDayRef = useRef(onSetActiveDay);
  const activeDayNumberRef = useRef(activeDayNumber);

  useEffect(() => {
    onItemClickRef.current = onItemClick;
  }, [onItemClick]);
  useEffect(() => {
    onSetActiveDayRef.current = onSetActiveDay;
  }, [onSetActiveDay]);
  useEffect(() => {
    activeDayNumberRef.current = activeDayNumber;
  }, [activeDayNumber]);

  const markers: MapMarker[] = [];
  for (const day of itinerary?.days || []) {
    // Number only the items that actually hit the map (have coords).
    // Counter advances per displayed stop so numbering matches what
    // the user sees on the timeline cards, which use the same filter.
    let stopIndex = 0;
    for (const item of day.items || []) {
      if (item.lat && item.lng) {
        stopIndex += 1;
        if (activeDayNumber && day.dayNumber !== activeDayNumber) continue;
        markers.push({
          itemId: item.id,
          lat: item.lat,
          lng: item.lng,
          title: item.title,
          dayNumber: day.dayNumber,
          stopIndex,
          time: item.time,
          type: item.type,
          description: item.description,
        });
      }
    }
  }

  // Stable signature so the effect re-runs when markers change — include
  // the active-day filter so we rebuild when the user switches days.
  const markerSignature =
    `active:${activeDayNumber ?? ""}|` +
    markers.map((m) => `${m.itemId}:${m.lat},${m.lng}`).join("|");

  // Main effect: build map, markers, polylines.
  useEffect(() => {
    if (!googleMapsApiKey || !mapRef.current || markers.length === 0) return;

    if (window.google?.maps) {
      initMap();
    } else {
      // Only one script load ever — the effect may re-run several
      // times during a streaming itinerary update (signature keeps
      // changing as partials arrive), and appending multiple
      // <script> tags produced by the Maps loader was leaving the
      // map half-initialised with no tiles. loadMapsScript dedupes
      // at the module level and runs initMap once the SDK is ready.
      loadMapsScript(googleMapsApiKey).then(() => {
        initMap();
      });
    }

    function initMap() {
      if (!mapRef.current || !window.google?.maps) return;

      const bounds = new google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));

      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          zoom: 12,
          center: bounds.getCenter(),
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          clickableIcons: false,
        });
      }
      if (!infoWindowRef.current) {
        infoWindowRef.current = new google.maps.InfoWindow({
          disableAutoPan: false,
        });
      }

      // Clear existing markers, polylines, and clusterer.
      markersByIdRef.current.forEach((m) => m.setMap(null));
      markersByIdRef.current.clear();
      markerMetaByIdRef.current.clear();
      polylinesRef.current.forEach((p) => p.setMap(null));
      polylinesRef.current = [];
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current.setMap(null);
        clustererRef.current = null;
      }

      const createdMarkers: google.maps.Marker[] = [];
      // Overview mode (no active day): unlabeled colored dots — numbers
      // would collide across days and create ambiguity. Focus mode: the
      // familiar numbered circles for just that day.
      const overview = !activeDayNumber;
      markers.forEach((m) => {
        const color = DAY_COLORS[(m.dayNumber - 1) % DAY_COLORS.length];
        const marker = new google.maps.Marker({
          position: { lat: m.lat, lng: m.lng },
          title: overview
            ? `Day ${m.dayNumber} — tap to focus`
            : `Day ${m.dayNumber}: ${m.title}`,
          label: overview
            ? undefined
            : {
                text: String(m.stopIndex),
                color: "#fff",
                fontSize: "11px",
                fontWeight: "bold",
              },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: overview ? 8 : 14,
            fillColor: color,
            fillOpacity: overview ? 0.9 : 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          },
        });

        marker.addListener("click", () => {
          // In overview, tapping a dot means "I want to look at this
          // day" — switch the whole app into day-focus mode. In focus
          // mode, taps select the individual item as before.
          if (activeDayNumberRef.current == null && onSetActiveDayRef.current) {
            onSetActiveDayRef.current(m.dayNumber);
            return;
          }
          openInfoWindow(m, color);
          onItemClickRef.current?.(m.itemId);
        });

        markersByIdRef.current.set(m.itemId, marker);
        markerMetaByIdRef.current.set(m.itemId, m);
        createdMarkers.push(marker);
      });

      // Attach markers to a clusterer — below ~8 markers the effect is
      // invisible, above ~20 it's a huge readability win. Algorithm
      // declusters automatically as the user zooms in.
      clustererRef.current = new MarkerClusterer({
        map: mapInstanceRef.current,
        markers: createdMarkers,
        algorithm: new SuperClusterAlgorithm({ maxZoom: 15, radius: 60 }),
        renderer: travelClusterRenderer,
      });

      // Per-day polylines connecting consecutive stops.
      const byDay = new Map<number, MapMarker[]>();
      for (const m of markers) {
        const list = byDay.get(m.dayNumber) || [];
        list.push(m);
        byDay.set(m.dayNumber, list);
      }
      byDay.forEach((dayMarkers, dayNumber) => {
        if (dayMarkers.length < 2) return;
        const color = DAY_COLORS[(dayNumber - 1) % DAY_COLORS.length];
        const line = new google.maps.Polyline({
          path: dayMarkers.map((m) => ({ lat: m.lat, lng: m.lng })),
          map: mapInstanceRef.current!,
          strokeColor: color,
          strokeOpacity: 0,
          strokeWeight: 2,
          icons: [
            {
              icon: {
                path: "M 0,-1 0,1",
                strokeOpacity: 0.8,
                strokeColor: color,
                scale: 3,
              },
              offset: "0",
              repeat: "14px",
            },
          ],
          clickable: false,
        });
        polylinesRef.current.push(line);
      });

      // Only retain bounds if it's non-empty — degenerate bounds
      // (no markers with valid coords) will crash fitBounds / intersects
      // downstream with "not a LatLngBounds" errors.
      if (!bounds.isEmpty()) {
        boundsRef.current = bounds;
      } else {
        boundsRef.current = null;
      }

      if (markers.length > 1 && !bounds.isEmpty()) {
        try {
          mapInstanceRef.current.fitBounds(bounds, {
            top: 40, bottom: 40, left: 40, right: 40,
          });
        } catch (err) {
          console.warn("fitBounds failed", err);
        }
      } else if (!bounds.isEmpty()) {
        mapInstanceRef.current.setCenter(bounds.getCenter());
        mapInstanceRef.current.setZoom(14);
      }

      // Pulse the "Fit to trip" control briefly when the user pans
      // all markers out of view, so they know there's a way back.
      google.maps.event.clearListeners(mapInstanceRef.current, "idle");
      mapInstanceRef.current.addListener("idle", () => {
        const map = mapInstanceRef.current;
        const trip = boundsRef.current;
        if (!map || !trip || !isValidBounds(trip)) return;
        const viewBounds = map.getBounds();
        if (!viewBounds) return;
        try {
          const overlaps =
            viewBounds.intersects(trip) ||
            viewBounds.contains(trip.getCenter());
          setRecenterHint(!overlaps);
        } catch {
          // Bounds might have gone stale (e.g., script reloaded in dev);
          // don't crash the whole page over a cosmetic hint.
          setRecenterHint(false);
        }
      });
    }
  }, [markerSignature, googleMapsApiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Imperative control handlers — defined after the map effect so they can
  // safely reference the refs.
  const fitToTrip = useCallback(() => {
    const map = mapInstanceRef.current;
    const trip = boundsRef.current;
    if (!map || !trip || !isValidBounds(trip)) return;
    try {
      map.fitBounds(trip, { top: 40, bottom: 40, left: 40, right: 40 });
    } catch (err) {
      console.warn("fitBounds failed", err);
    }
    setRecenterHint(false);
  }, []);

  const goToMyLocation = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !navigator.geolocation) return;

    const apply = (pos: GeolocationPosition) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      userLocationRef.current = { lat, lng };
      if (!userLocationMarkerRef.current && window.google?.maps) {
        userLocationMarkerRef.current = new google.maps.Marker({
          map,
          position: { lat, lng },
          title: "Your location",
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: "#2ecbff",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 3,
          },
          zIndex: 2000,
        });
      } else {
        userLocationMarkerRef.current?.setPosition({ lat, lng });
      }
      map.panTo({ lat, lng });
      const currentZoom = map.getZoom() ?? 12;
      if (currentZoom < 14) map.setZoom(14);
      setGeoDenied(false);
    };

    // Fast-path: if we already have the coords, just re-pan without
    // prompting permissions again.
    if (userLocationRef.current) {
      map.panTo(userLocationRef.current);
      const currentZoom = map.getZoom() ?? 12;
      if (currentZoom < 14) map.setZoom(14);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      apply,
      () => setGeoDenied(true),
      { timeout: 10000, maximumAge: 60_000 }
    );
  }, []);

  // Auto-hide "Location unavailable" toast after a few seconds.
  useEffect(() => {
    if (!geoDenied) return;
    const t = window.setTimeout(() => setGeoDenied(false), 3000);
    return () => window.clearTimeout(t);
  }, [geoDenied]);

  // Active marker effect: when focus/selection changes, update marker sizes
  // and pulse animation.
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    const activeId = focusedItemId || selectedItemId || null;

    // Reset all markers to default size.
    markersByIdRef.current.forEach((marker, itemId) => {
      const meta = markerMetaByIdRef.current.get(itemId);
      if (!meta) return;
      const color = DAY_COLORS[(meta.dayNumber - 1) % DAY_COLORS.length];
      const isActive = itemId === activeId;
      marker.setIcon({
        path: google.maps.SymbolPath.CIRCLE,
        scale: isActive ? 18 : 14,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: "#fff",
        strokeWeight: isActive ? 3 : 2,
      });
      marker.setZIndex(isActive ? 1000 : 1);
    });

    // Clean up previous pulse circle and interval.
    if (pulseIntervalRef.current) {
      window.clearInterval(pulseIntervalRef.current);
      pulseIntervalRef.current = null;
    }
    if (pulseCircleRef.current) {
      pulseCircleRef.current.setMap(null);
      pulseCircleRef.current = null;
    }

    if (!activeId) return;
    const activeMarker = markersByIdRef.current.get(activeId);
    const activeMeta = markerMetaByIdRef.current.get(activeId);
    if (!activeMarker || !activeMeta) return;

    // Pan map to the active marker. For sticky selections (clicks from
    // card taps / info windows), always ensure a comfortable close-in
    // zoom so the pulse animation and surroundings are legible — not
    // just when the marker was buried inside a cluster. Hovers don't
    // zoom, to avoid jumpy map behavior as the cursor moves.
    mapInstanceRef.current.panTo({
      lat: activeMeta.lat,
      lng: activeMeta.lng,
    });
    const isSticky = selectedItemId && activeId === selectedItemId;
    if (isSticky) {
      const currentZoom = mapInstanceRef.current.getZoom() ?? 12;
      if (currentZoom < 15) {
        mapInstanceRef.current.setZoom(15);
      }
    }

    // Pulsing circle effect.
    const color = DAY_COLORS[(activeMeta.dayNumber - 1) % DAY_COLORS.length];
    pulseCircleRef.current = new google.maps.Circle({
      map: mapInstanceRef.current,
      center: { lat: activeMeta.lat, lng: activeMeta.lng },
      radius: 30,
      fillColor: color,
      fillOpacity: 0.3,
      strokeColor: color,
      strokeOpacity: 0.6,
      strokeWeight: 2,
      clickable: false,
    });
    pulseStepRef.current = 0;
    pulseIntervalRef.current = window.setInterval(() => {
      if (!pulseCircleRef.current) return;
      pulseStepRef.current = (pulseStepRef.current + 1) % 40;
      const t = pulseStepRef.current / 40;
      // Expand radius 30 -> 120m, fade opacity 0.4 -> 0
      pulseCircleRef.current.setRadius(30 + t * 90);
      pulseCircleRef.current.setOptions({
        fillOpacity: 0.4 * (1 - t),
        strokeOpacity: 0.6 * (1 - t),
      });
    }, 60);

    // If this is a sticky selection, also open the info window.
    if (selectedItemId && activeId === selectedItemId) {
      openInfoWindow(activeMeta, color);
    }

    return () => {
      if (pulseIntervalRef.current) {
        window.clearInterval(pulseIntervalRef.current);
        pulseIntervalRef.current = null;
      }
      if (pulseCircleRef.current) {
        pulseCircleRef.current.setMap(null);
        pulseCircleRef.current = null;
      }
    };
  }, [focusedItemId, selectedItemId, markerSignature]);

  function openInfoWindow(m: MapMarker, color: string) {
    if (!infoWindowRef.current) return;
    // On mobile the map is short (~32vh); the info window would cover
    // nearly all of it and fight with the card that expands below.
    // Skip the popup on narrow viewports — the card has the same info.
    if (typeof window !== "undefined" && window.innerWidth < 768) return;
    const safe = (s: string | undefined) =>
      (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const html = `
      <div style="font-family:system-ui,-apple-system,sans-serif;max-width:220px">
        <div style="color:${color};font-size:11px;font-weight:600;margin-bottom:2px">
          Day ${m.dayNumber}${m.time ? " \u00b7 " + safe(m.time) : ""}
        </div>
        <div style="font-weight:600;font-size:14px;color:#1e293b;line-height:1.3">
          ${safe(m.title)}
        </div>
        ${
          m.description
            ? `<div style="font-size:12px;color:#64748b;margin-top:4px;line-height:1.4">${safe(m.description)}</div>`
            : ""
        }
      </div>
    `;
    const marker = markersByIdRef.current.get(m.itemId);
    if (!marker || !mapInstanceRef.current) return;
    infoWindowRef.current.setContent(html);
    infoWindowRef.current.open({
      anchor: marker,
      map: mapInstanceRef.current,
    });
  }

  if (!googleMapsApiKey) {
    return (
      <div className="rounded-lg border border-white/15 bg-white/5 p-6 text-center text-ink-light text-sm">
        <p>Map view requires Google Maps API key</p>
        <p className="text-xs mt-1 text-ink-light/85">Set VITE_GOOGLE_MAPS_API_KEY to enable</p>
      </div>
    );
  }

  if (markers.length === 0) {
    return (
      <div className="rounded-lg border border-white/15 bg-white/5 p-6 text-center text-ink-light text-sm">
        No locations with coordinates yet
      </div>
    );
  }

  const geoSupported =
    typeof navigator !== "undefined" && !!navigator.geolocation;

  return (
    <div
      className={`relative w-full ${heightClass} rounded-lg border border-white/15 overflow-hidden`}
    >
      <div ref={mapRef} className="absolute inset-0" />

      {/* Control stack — top-left, out of the way of Google's built-in
          zoom/fullscreen controls (bottom-right). */}
      <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-10">
        <MapControlButton
          label="Fit to trip"
          pulse={recenterHint}
          onClick={fitToTrip}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
              <path d="M4 10V4h6" />
              <path d="M20 10V4h-6" />
              <path d="M4 14v6h6" />
              <path d="M20 14v6h-6" />
            </svg>
          }
        />
        {geoSupported && (
          <MapControlButton
            label="My location"
            onClick={goToMyLocation}
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 2v3" />
                <path d="M12 19v3" />
                <path d="M2 12h3" />
                <path d="M19 12h3" />
              </svg>
            }
          />
        )}
      </div>

      {geoDenied && (
        <div
          role="status"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-deep/80 border border-white/20 text-xs text-ink shadow-lg backdrop-blur-sm z-10"
        >
          Location unavailable
        </div>
      )}
    </div>
  );
}

function MapControlButton({
  label,
  icon,
  onClick,
  pulse,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  pulse?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={`w-10 h-10 inline-flex items-center justify-center rounded-lg bg-deep/75 border border-white/20 text-ink hover:bg-deep/90 hover:border-white/40 backdrop-blur-sm shadow-md transition-colors ${
        pulse ? "animate-pulse ring-2 ring-accent/60" : ""
      }`}
    >
      {icon}
    </button>
  );
}

// Custom renderer for cluster icons — glassy accent-colored disc with a
// count label. Clicking on a cluster triggers the default zoom-in
// behavior via MarkerClusterer. zIndex scales with cluster count so
// larger clusters render above smaller ones.
// Defensive check before passing a bounds object back into Google Maps:
// guards against null/empty/foreign bounds that would otherwise produce
// "not a LatLngBounds or LatLngBoundsLiteral" runtime errors.
function isValidBounds(b: unknown): b is google.maps.LatLngBounds {
  if (!b || typeof b !== "object") return false;
  const maybe = b as google.maps.LatLngBounds;
  if (typeof maybe.isEmpty !== "function") return false;
  try {
    return !maybe.isEmpty();
  } catch {
    return false;
  }
}

const travelClusterRenderer: Renderer = {
  render({ count, position }, stats) {
    const size =
      count < 10 ? 38 : count < 25 ? 46 : count < 50 ? 54 : 62;
    const svg = window.btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="${size}" height="${size}">
        <defs>
          <radialGradient id="g" cx="50%" cy="40%" r="55%">
            <stop offset="0%" stop-color="#7de6ff" stop-opacity="1"/>
            <stop offset="60%" stop-color="#2ecbff" stop-opacity="1"/>
            <stop offset="100%" stop-color="#1099de" stop-opacity="0.95"/>
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="30" fill="url(#g)" stroke="#fff" stroke-width="3" opacity="0.95"/>
      </svg>
    `);

    return new google.maps.Marker({
      position,
      icon: {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      },
      label: {
        text: String(count),
        color: "#04111a",
        fontSize: count < 100 ? "13px" : "11px",
        fontWeight: "700",
      },
      title: `${count} places`,
      zIndex: 100 + Math.floor(stats.clusters.markers.mean),
    });
  },
};

declare global {
  interface Window {
    google: typeof google;
  }
}

// Module-scoped dedupe for the Google Maps script tag. The
// ItineraryMap effect can re-run many times during a streaming
// itinerary update — loading the SDK more than once leaves it in a
// broken state ("Google Maps already loaded" + silent tile failure).
let mapsScriptPromise: Promise<void> | null = null;

function loadMapsScript(apiKey: string): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (mapsScriptPromise) return mapsScriptPromise;

  mapsScriptPromise = new Promise<void>((resolve, reject) => {
    // Reuse an existing tag if one happens to be in the DOM already.
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-roam-maps="1"]'
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("maps load failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`;
    script.async = true;
    script.defer = true;
    script.dataset.roamMaps = "1";
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => {
      mapsScriptPromise = null;
      reject(new Error("maps load failed"));
    });
    document.head.appendChild(script);
  });
  return mapsScriptPromise;
}
